import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import supabase from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { fetchAppSettings } from '../hooks/useAppSettings';

// Helper function for promise timeout
const withTimeout = (promise, ms, timeoutError = new Error('Promise timed out')) => {
  const timeout = new Promise((_, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id);
      reject(timeoutError);
    }, ms);
  });
  return Promise.race([promise, timeout]);
};

const PROFILE_FETCH_TIMEOUT = 7000; // 7 seconds

// Create context with default values
const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,       // True when initially checking auth session
  profile: null,
  subscription: null,  // User's active/trialing subscription
  hasProAccess: false, // True if user has access to Pro features
  isFreeMode: false,   // True when both paid plans are disabled (Pro is free for all logged-in users)
  proAccessLoading: true, // True when checking profile/subscription for Pro access
  setLoading: () => {},
  signUp: async () => ({ error: new Error('Auth not initialized') }),
  signIn: async () => ({ error: new Error('Auth not initialized') }),
  signOut: async () => ({ error: new Error('Auth not initialized') }),
  signInWithGoogle: async () => ({ error: new Error('Auth not initialized') }),
  resetPassword: async () => ({ error: new Error('Auth not initialized') }),
  updatePassword: async () => ({ error: new Error('Auth not initialized') }),
  showAdvanced: false,
  updateAdvancedFeatures: async () => {}
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true); // Overall auth loading
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null); // New state
  const [hasProAccess, setHasProAccess] = useState(false); // New state
  const [proAccessLoading, setProAccessLoading] = useState(true); // New state for pro access check
  const [isInFreeTrial, setIsInFreeTrial] = useState(false);
  const [isFreeMode, setIsFreeMode] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [trialStartedAt, setTrialStartedAt] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const [event, setEvent] = useState(null); // New state to store the event type
  const navigate = useNavigate();

  // New function to fetch profile, subscription, and determine pro access
  const fetchProfileAndSubscriptionAndDetermineAccess = async (currentSession) => {
    if (!currentSession?.user) {
      setProfile(null);
      setSubscription(null);
      setHasProAccess(false);
      setProAccessLoading(false); // Reset here too
      setLoading(false); // Also set main loading false if no user in session
      return;
    }

    setProAccessLoading(true);
    setLoading(true); // Ensure main loading is active while FPADAA runs

    let userProfile = null;
    let userSubscription = null;
    let calculatedProAccess = false;

    try {
      // Granular try-catch for profile fetch
      let profileDataToUse, profileErrorToUse;
      try {
        const profilePromise = supabase
          .from('profiles')
          .select('*, is_subscribed, subscription_status, is_admin, has_lifetime_access') // Explicitly select all needed fields
          .eq('id', currentSession.user.id)
          .single();
        
        const response = await withTimeout(
          profilePromise,
          PROFILE_FETCH_TIMEOUT,
          new Error(`[FPADAA] Profile fetch timed out after ${PROFILE_FETCH_TIMEOUT}ms for user ID: ${currentSession.user.id}`)
        );

        profileDataToUse = response.data;
        profileErrorToUse = response.error;
      } catch (e) {
        if (e.message.includes('Profile fetch timed out')) {
        } else {
        }
        // Ensure all loading states are reset and we exit FPADAA
        setProfile(null);
        setSubscription(null);
        setHasProAccess(false);
        setProAccessLoading(false); 
        setLoading(false); // Also set main loading to false here as a critical safety net
        return; // Exit FPADAA early
      }
      
      // Use profileDataToUse and profileErrorToUse from the inner try-catch

      if (profileErrorToUse && profileErrorToUse.code !== 'PGRST116') {
      } else if (!profileDataToUse) {
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert([{ 
            id: currentSession.user.id,
            email: currentSession.user.email,
            full_name: currentSession.user.user_metadata?.full_name || '',
            // New profiles won't have subscription status yet, defaults in DB schema should apply
          }])
          .select('*, is_subscribed, subscription_status, is_admin, has_lifetime_access') // Match the explicit select here too
          .single();
        if (insertError) {
        } else {
          profileDataToUse = newProfile;
        }
      }
      userProfile = profileDataToUse;
      setProfile(userProfile);
      setShowAdvanced(!!userProfile?.show_advanced_features);

      // Block login for accounts pending deletion
      if (userProfile?.is_pending_deletion === true) {
        setHasProAccess(false);
        setProAccessLoading(false);
        setLoading(false);
        await supabase.auth.signOut();
        navigate('/', { state: { deletionPending: true } });
        return;
      }

      // If profile fetch failed catastrophically and we returned, this part won't run.
      // If profile was just not found and created, or fetched, continue to subscription.
      if (!userProfile && (profileErrorToUse && profileErrorToUse.code !== 'PGRST116') ) {
        // This condition handles if profile fetch had an error (not PGRST116) and profile is null
        // The critical error catch above should handle most hangs/errors, but this is a safeguard
      } else {
        const { data: subData, error: subError } = await supabase
          .from('subscriptions')
          .select('status')
          .eq('user_id', currentSession.user.id)
          .in('status', ['active'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subError) {
        }
        userSubscription = subData || null;
        setSubscription(userSubscription);

        // Check if within free trial window from trial_started_at (duration from app_settings)
        const appSettings = await fetchAppSettings();
        const TRIAL_DAYS = appSettings.trial_days || 14;
        // Free mode: when both paid plans are disabled, Pro is free for any logged-in user
        const freeMode = !appSettings.annual_enabled && !appSettings.monthly_enabled;
        setIsFreeMode(freeMode);
        const trialStart = userProfile?.trial_started_at || null;
        setTrialStartedAt(trialStart);
        const trialEnd = trialStart
          ? new Date(new Date(trialStart).getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000)
          : null;
        const inTrial = trialEnd ? new Date() < trialEnd : false;
        setIsInFreeTrial(inTrial);
        setTrialEndsAt(trialEnd ? trialEnd.toISOString() : null);

        calculatedProAccess = !!(
          freeMode ||
          userProfile?.is_admin === true ||
          userProfile?.has_lifetime_access === true ||
          inTrial ||
          (userSubscription && userSubscription.status === 'active')
        );
        setHasProAccess(calculatedProAccess);

      }
    } catch (error) {
      setHasProAccess(false); // Default to no access on error
    } finally {
      setProAccessLoading(false);
      setLoading(false); // Main loading state
    }
  }; // End of fetchProfileAndSubscriptionAndDetermineAccess

  useEffect(() => {
    const getInitialSession = async () => {
      // setLoading(true); // Already true by default, or managed by specific flows

      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();

        if (error) {
          setUser(null);
          setSession(null);
          setProfile(null);
          setSubscription(null);
          setHasProAccess(false);
          // setLoading(false); // Defer to onAuthStateChange
          // setProAccessLoading(false); // Defer to onAuthStateChange or FPADAA completion
          return; // Exit if error
        }

        if (currentSession) {
          setUser(currentSession.user);
          setSession(currentSession);
          // setLoading(true); // Ensure loading remains true while fetching profile
          // setProAccessLoading(true); // FPADAA will handle this
          // await fetchProfileAndSubscriptionAndDetermineAccess(currentSession); // This will be triggered by onAuthStateChange
        } else {
          setUser(null);
          setSession(null);
          setProfile(null);
          setSubscription(null);
          setHasProAccess(false);
          // setLoading(false); // Defer to onAuthStateChange
          // setProAccessLoading(false); // Defer to onAuthStateChange
        }
      } catch (e) {
        setUser(null);
        setSession(null);
        setProfile(null);
        setSubscription(null);
        setHasProAccess(false);
        // setLoading(false); // Defer to onAuthStateChange
        // setProAccessLoading(false); // Defer to onAuthStateChange
      } finally {
        // setLoading(false); // IMPORTANT: Removed this. Let onAuthStateChange handle it.
      }
    };

    getInitialSession();

    // Correctly destructure to get the subscription object
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {

        setEvent(event); // Store the event type

        // Always update user and session state first
        setUser(currentSession?.user ?? null);
        setSession(currentSession);

        if (event === 'PASSWORD_RECOVERY') {
          // Handle password recovery event if needed
          setLoading(false); // Ensure loading stops
          setProAccessLoading(false);
        } else if (event === 'SIGNED_IN') {
          if (currentSession?.user) {
            // Only fetch profile if not already loaded (avoids loading flash on tab refocus/token refresh)
            setUser(currentSession.user);
            setSession(currentSession);
            setProfile((prevProfile) => {
              if (!prevProfile) {
                // Genuine new login — fetch in background without blocking UI
                fetchProfileAndSubscriptionAndDetermineAccess(currentSession);
              }
              return prevProfile;
            });
          } else {
            // Clear potentially stale data if SIGNED_IN has no user
            setProfile(null);
            setSubscription(null);
            setHasProAccess(false);
            setLoading(false);
            setProAccessLoading(false);
          }
        } else if (event === 'INITIAL_SESSION' && currentSession?.user) {
          setUser(currentSession.user);
          setSession(currentSession);
          setEvent('INITIAL_SESSION'); // Set the event state

          await fetchProfileAndSubscriptionAndDetermineAccess(currentSession);
          // setLoading(false); // REMOVED: FPADAA handles this.
          // setProAccessLoading(false); // REMOVED: FPADAA handles this.
          //  // Log cleanup
        } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          // Explicitly clear all user-related state
          setUser(null);
          setSession(null);
          setProfile(null);
          setSubscription(null);
          setHasProAccess(false);
          setLoading(false);
          setProAccessLoading(false);
        } else if (event === 'USER_UPDATED' && currentSession?.user) {
          await fetchProfileAndSubscriptionAndDetermineAccess(currentSession);
          // setLoading might not be needed here unless USER_UPDATED implies a loading state
        } else {
          // Catch-all for other events or unexpected null sessions
          setLoading(false);
          // Decide if proAccessLoading should be false here too, likely yes
          setProAccessLoading(false);
        }
      }
    );

    return () => {
      // Call unsubscribe on the correct object
      subscription?.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount


  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) return { error };
      navigate('/');
      return { error: null };
    } catch (error) { return { error }; }
    finally { setLoading(false); }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  };

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: fullName } },
    });
    return { data, error };
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const redirectBaseUrl = window.location.origin;
      localStorage.setItem('authRedirectUrl', redirectBaseUrl + (window.location.pathname === '/' ? '' : window.location.pathname) );
      localStorage.setItem('googleAuthInProgress', 'true');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectBaseUrl,
          scopes: 'email profile',
          queryParams: { access_type: 'offline', prompt: 'consent' }
        },
      });
      if (error) {
        localStorage.removeItem('googleAuthInProgress');
        localStorage.removeItem('authRedirectUrl');
        setLoading(false);
        return { error };
      }
      return { data };
    } catch (err) {
      localStorage.removeItem('googleAuthInProgress');
      localStorage.removeItem('authRedirectUrl');
      setLoading(false);
      return { error: err };
    }
  };

  const resetPassword = async (email) => {
    setLoading(true);
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    setLoading(false);
    return { data, error };
  };

  const updatePassword = async (newPassword) => {
    setLoading(true);
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    setLoading(false);
    return { data, error };
  };

  const updateAdvancedFeatures = async (value) => {
    setShowAdvanced(value);
    setProfile(prev => prev ? { ...prev, show_advanced_features: value } : prev);
    if (!user?.id) return;
    supabase
      .from('profiles')
      .update({ show_advanced_features: value })
      .eq('id', user.id)
      .then(() => {});
  };

  const startTrial = async () => {
    if (!user?.id) return { error: new Error('Not logged in') };
    if (trialStartedAt) return { error: new Error('Trial already started') };
    const now = new Date().toISOString();
    const { error } = await supabase
      .from('profiles')
      .update({ trial_started_at: now })
      .eq('id', user.id);
    if (error) return { error };
    setTrialStartedAt(now);
    const appSettings = await fetchAppSettings();
    const trialDays = appSettings.trial_days || 14;
    const trialEnd = new Date(new Date(now).getTime() + trialDays * 24 * 60 * 60 * 1000);
    setIsInFreeTrial(true);
    setTrialEndsAt(trialEnd.toISOString());
    setHasProAccess(true);
    setProfile(prev => prev ? { ...prev, trial_started_at: now } : prev);
    return { error: null };
  };

  const value = useMemo(() => ({
    user,
    session,
    loading,
    profile,
    subscription,
    hasProAccess,
    isFreeMode,
    proAccessLoading,
    isInFreeTrial,
    trialEndsAt,
    trialStartedAt,
    showAdvanced,
    updateAdvancedFeatures,
    startTrial,
    setLoading,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    resetPassword,
    updatePassword
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 3.4.2026
  }), [user, session, loading, profile, subscription, hasProAccess, isFreeMode, proAccessLoading, isInFreeTrial, trialEndsAt, trialStartedAt, showAdvanced]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
} // This is the closing brace for AuthProvider
